import axios from 'axios';
import { API_CONFIG } from './config';

// Sistema de token memoizado con promesa compartida
class TokenManager {
  private tokenCache: string | null = null;
  private tokenPromise: Promise<string> | null = null;
  private tokenExpiry: number = 0;
  private getTokenFn: (() => Promise<string>) | null = null;

  setTokenFunction(getToken: () => Promise<string>) {
    this.getTokenFn = getToken;
  }

  async getToken(): Promise<string> {
    if (this.tokenCache && Date.now() < this.tokenExpiry) {
      return this.tokenCache;
    }

    if (this.tokenPromise) {
      return this.tokenPromise;
    }

    if (!this.getTokenFn) {
      throw new Error('Token function no configurada');
    }

    this.tokenPromise = this.getTokenFn()
      .then((token) => {
        this.tokenCache = token;
        this.tokenExpiry = Date.now() + (50 * 60 * 1000);
        this.tokenPromise = null;
        return token;
      })
      .catch((error) => {
        this.tokenPromise = null;
        throw error;
      });

    return this.tokenPromise;
  }

  clearToken() {
    this.tokenCache = null;
    this.tokenExpiry = 0;
    this.tokenPromise = null;
  }
}

const tokenManager = new TokenManager();

export const axiosInstance = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

let auth0Logout: (() => void) | null = null;

export const setupAxiosAuth = (getToken: () => Promise<string>, logout: () => void) => {
  tokenManager.setTokenFunction(getToken);
  auth0Logout = logout;

  axiosInstance.interceptors.request.use(
    async (config) => {
      try {
        const token = await tokenManager.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      } catch (error) {
        console.error('Error al obtener el token:', error);
        return config;
      }
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      // Manejo de usuario no encontrado en el sistema
      if (error.response?.status === 403) {
        console.log('Usuario no encontrado en el sistema, cerrando sesión...');
        if (auth0Logout) {
          auth0Logout();
        }
        return Promise.reject(error);
      }

      // Manejo de token expirado
      if (error.response?.status === 401) {
        try {
          tokenManager.clearToken();
          const token = await tokenManager.getToken();
          if (token && error.config) {
            error.config.headers.Authorization = `Bearer ${token}`;
            return axiosInstance.request(error.config);
          }
        } catch (refreshError) {
          console.error('Error al renovar el token:', refreshError);
        }
      }
      return Promise.reject(error);
    }
  );
}; 